import type { UseQueryOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { add, formatISO } from "date-fns";

import { jsonRequest } from "@/lib/network/json-request";

import { groupLssSlotsByDate } from "./lss.utils";

type SlotProvider = {
  flareId: string;
  id: string;
  name: string;
  rank: number;
  source: string;
};

type SlotMatcherResultsArray = {
  rank: number;
  slotMatcherType: string;
};

type SlotMatcherResultsProvider = {
  slotMatcherResults?: SlotMatcherResultsArray[];
  slotTotalRank?: number;
};

export type LssSlot = {
  capacity?: number;
  end: Date | string;
  providers?: SlotProvider[];
  slotMatcherResults?: SlotMatcherResultsProvider;
  start: string;
};

type RequestProps = {
  bookitRequestId: string;
  from?: Date;
  to?: Date;
};

type UseLssSlotsProps = Omit<
  UseQueryOptions<LssSlot[]>,
  "queryKey" | "queryFn" | "select" | "enabled"
> & {
  bookitRequestId: string;
  from?: Date;
  to?: Date;
};

function getISODate(date: Date) {
  return formatISO(new Date(date), { representation: "date" });
}

async function request({
  bookitRequestId,
  from,
  to,
}: RequestProps): Promise<LssSlot[]> {
  if (!from || !bookitRequestId) {
    throw new Error("Invalid 'from' or 'bookitRequestId' parameters");
  }

  const fromISO = getISODate(from);
  const toISO = getISODate(
    to === undefined ? add(new Date(from), { days: 14 }) : to,
  );

  const url = new URL(
    `${process.env.NEXT_PUBLIC_BOOKIT_BASE_URL}/appointments/${bookitRequestId}/slots`,
  );
  url.searchParams.set("from", fromISO);
  url.searchParams.set("to", toISO);

  return jsonRequest<LssSlot[]>(url.toString());
}

/**
 * Returns a query hook for fetching LSS slots by bookit request ID.
 * For fetching LSS slots not by bookit request ID, use useGetAvailableSlots instead.
 *
 * @param bookitRequestId - The bookit request/meeting ID of the LSS meeting to fetch.
 */
export function useLssSlotsByRequestId({
  from,
  to,
  bookitRequestId,
  ...options
}: UseLssSlotsProps) {
  return useQuery({
    ...options,
    queryKey: ["fetch-lss-slots-by-meeting-id", bookitRequestId, from],
    queryFn: () => request({ bookitRequestId, from, to }),
    enabled: Boolean(from && bookitRequestId),
    select: (slots) => {
      return groupLssSlotsByDate(slots);
    },
  });
}
